import { useState } from 'react';
import { useTranslation } from 'react-i18next';
import { resolveApiErrorMessage } from '@/api/resolveApiErrorMessage';
import type { TenantDetail } from '@/api/tenants/tenantsApi.types';
import { Button } from '@/components/ui/button';
import { Card } from '@/components/ui/card';
import { Icon } from '@/components/common/Icon';
import { ConfirmDialog } from '@/components/ui/confirmDialog';
import { useHasPlatformPermission } from '@/hooks/useHasPlatformPermission';
import {
  useActivateTenantMutation,
  useSuspendTenantMutation,
} from '@/hooks/useTenantLifecycleMutations';
import { formatDateToDayMonthYear } from '@/utils/dateFormatting';
import { showErrorNotification, showSuccessNotification } from '@/utils/notifications';

interface OverviewTabProps {
  tenant: TenantDetail;
}

export function OverviewTab({ tenant }: OverviewTabProps) {
  const { t } = useTranslation('modules');
  const suspendMutation = useSuspendTenantMutation();
  const activateMutation = useActivateTenantMutation();
  const canSuspend = useHasPlatformPermission('tenant.suspend');
  const canActivate = useHasPlatformPermission('tenant.update');
  const [isConfirmingSuspend, setIsConfirmingSuspend] = useState(false);

  const isSuspended = tenant.status === 'suspended';
  const typeLabel =
    tenant.type === 'organization' ? t('admin.typeOrganization') : t('admin.typeSolo');

  function suspend() {
    suspendMutation.mutate(tenant.id, {
      onSuccess: function done() {
        showSuccessNotification(t('admin.suspendedNotice'));
        setIsConfirmingSuspend(false);
      },
      onError: function fail(suspendError) {
        showErrorNotification(resolveApiErrorMessage(suspendError));
        setIsConfirmingSuspend(false);
      },
    });
  }

  function activate() {
    activateMutation.mutate(tenant.id, {
      onSuccess: function done() {
        showSuccessNotification(t('admin.activatedNotice'));
      },
      onError: function fail(activateError) {
        showErrorNotification(resolveApiErrorMessage(activateError));
      },
    });
  }

  return (
    <div className="grid grid-cols-1 lg:grid-cols-2 gap-5 items-start">
      <div className="space-y-5">
        <Card padding="md">
          <h2 className="text-sm font-bold text-zooindigo mb-4">{t('admin.overviewTitle')}</h2>
          <dl className="grid grid-cols-1 sm:grid-cols-2 gap-x-10 gap-y-3">
            <OverviewRow label={t('admin.codeLabel')} value={tenant.code} />
            <OverviewRow label={t('admin.typeLabel')} value={typeLabel} />
            <OverviewRow
              label={t('admin.businessNameLabel')}
              value={tenant.businessName === null ? t('admin.notSet') : tenant.businessName}
            />
            <OverviewRow
              label={t('admin.languageLabel')}
              value={tenant.defaultLanguage.toUpperCase()}
            />
            <OverviewRow
              label={t('admin.createdLabel')}
              value={formatDateToDayMonthYear(new Date(tenant.createdAt))}
            />
            <OverviewRow
              label={t('admin.moduleCountLabel')}
              value={String(tenant.activeModuleCount)}
            />
          </dl>
        </Card>

        <Card padding="md">
          <h2 className="text-sm font-bold text-zooindigo mb-1">{t('admin.statusTitle')}</h2>
          <p className="text-xs text-zoomuted mb-4">
            {isSuspended === true ? t('admin.statusSuspendedHint') : t('admin.statusActiveHint')}
          </p>
          {isSuspended === true
            ? canActivate === true && (
                <Button
                  className="flex items-center gap-2"
                  isLoading={activateMutation.isPending}
                  onClick={activate}
                >
                  <Icon name="RefreshCw" />
                  {t('admin.reactivateWorkspace')}
                </Button>
              )
            : canSuspend === true && (
                <Button
                  variant="destructiveGhost"
                  className="flex items-center gap-2 border border-zooborder"
                  onClick={function openConfirm() {
                    setIsConfirmingSuspend(true);
                  }}
                >
                  <Icon name="Lock" />
                  {t('admin.suspendWorkspace')}
                </Button>
              )}
        </Card>
      </div>

      <Card padding="md">
        <h2 className="text-sm font-bold text-zooindigo mb-4">{t('admin.ownerTitle')}</h2>
        {tenant.ownerName === null ? (
          <p className="text-sm text-zoomuted">{t('admin.noOwner')}</p>
        ) : (
          <div className="flex items-center gap-3">
            <span className="w-10 h-10 rounded-full bg-zoovpalelavender text-zooprimary flex items-center justify-center shrink-0">
              <Icon name="User" />
            </span>
            <div className="min-w-0">
              <div className="text-sm font-bold text-zooindigo truncate">{tenant.ownerName}</div>
              <div className="text-xs text-zoomuted truncate">
                {tenant.ownerEmail === null ? '' : tenant.ownerEmail}
              </div>
            </div>
          </div>
        )}

        <div className="mt-4 pt-4 border-t border-zooborder">
          <p className="text-[10px] font-bold uppercase tracking-wider text-zoomuted mb-1">
            {t('admin.membersLabel')}
          </p>
          <p className="text-sm text-zooindigo">{String(tenant.memberCount)}</p>
        </div>
      </Card>

      {isConfirmingSuspend === true ? (
        <ConfirmDialog
          tone="danger"
          iconName="Lock"
          title={t('admin.suspendTitle')}
          description={t('admin.suspendDescription', { name: tenant.name })}
          confirmLabel={t('admin.suspendWorkspace')}
          isConfirming={suspendMutation.isPending}
          onConfirm={suspend}
          onCancel={function cancel() {
            setIsConfirmingSuspend(false);
          }}
        />
      ) : null}
    </div>
  );
}

interface OverviewRowProps {
  label: string;
  value: string;
}

function OverviewRow({ label, value }: OverviewRowProps) {
  return (
    <div className="flex items-center justify-between gap-4 text-sm">
      <dt className="text-zoomuted">{label}</dt>
      <dd className="font-semibold text-zooindigo">{value}</dd>
    </div>
  );
}
